/**
 * Utility functions for handling year-specific data display
 */

import { YEAR_CONFIG } from '../config/yearConfig';

/**
 * Get configured year used when "All Years" needs a single display/calculation year.
 * @returns {String}
 */
export const getConfiguredAllYearsYear = () => String(YEAR_CONFIG.lastDataYear);

/**
 * Get year-specific data for a client based on selected year
 * @param {Object} client - Client object
 * @param {String} selectedYear - Selected year ('All' or specific year like '2024')
 * @returns {Object} Object with rooms, roomNights, and revenue for the selected year
 */
export const getYearSpecificData = (client, selectedYear) => {
  // When 'All' is selected, use configured last data year instead of runtime calendar year
  const yearToUse = selectedYear === 'All' ? getConfiguredAllYearsYear() : selectedYear;
  
  const roomsColumn = `${yearToUse} Rooms`;
  const rnColumn = `${yearToUse} RN`;
  const revenueColumn = `${yearToUse} Revenue`;
  
  return {
    rooms: client[roomsColumn] || 0,
    roomNights: client[rnColumn] || 0,
    revenue: client[revenueColumn] || '$0.00'
  };
};

/**
 * Get year-specific labels for display
 * @param {String} selectedYear - Selected year ('All' or specific year like '2024')
 * @returns {Object} Object with display labels for rooms, roomNights, and revenue
 */
export const getYearSpecificLabels = (selectedYear) => {
  if (selectedYear === 'All') {
    return {
      rooms: 'Total Rooms',
      roomNights: 'Total RN',
      revenue: 'Total Revenue'
    };
  } else {
    return {
      rooms: `${selectedYear} Rooms`,
      roomNights: `${selectedYear} RN`,
      revenue: `${selectedYear} Revenue`
    };
  }
};

/**
 * Get the current year
 * @returns {String} Current year as string
 */
export const getCurrentYear = () => {
  return new Date().getFullYear().toString();
};

/**
 * Calculate year-specific totals for a manager and year
 * @param {Array} clientData - Array of all client data
 * @param {String} activeManager - Active manager name
 * @param {String} year - Year to calculate totals for
 * @returns {Object} Object with totals for rooms, rn, revenue, and salesGoal
 */
export const calculateYearSpecificTotals = (clientData, activeManager, year) => {
  if (!clientData || !activeManager || !year) {
    return {
      rooms: 0,
      rn: 0,
      revenue: '$0.00',
      salesGoal: 'n/a'
    };
  }

  // Filter clients for the active manager
  const managerClients = clientData.filter(client => 
    client.conference_manager === activeManager
  );

  let totalRooms = 0;
  let totalRN = 0;
  let totalRevenue = 0;
  let totalSalesGoal = 0;
  let hasValidSalesGoal = false;

  managerClients.forEach(client => {
    const yearData = getYearSpecificData(client, year);
    
    totalRooms += parseInt(yearData.rooms) || 0;
    totalRN += parseInt(yearData.roomNights) || 0;
    
    // Parse revenue (remove $ and commas)
    const revenueStr = yearData.revenue.toString().replace(/[$,]/g, '');
    totalRevenue += parseFloat(revenueStr) || 0;

    // Get sales goal from Excel data
    const salesGoalValue = client['Sales goal (INTEGER)'];
    if (salesGoalValue && !isNaN(salesGoalValue) && salesGoalValue > 0) {
      totalSalesGoal += parseFloat(salesGoalValue);
      hasValidSalesGoal = true;
    }
  });

  // Format revenue
  const formattedRevenue = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(totalRevenue);

  // Format sales goal from Excel data or show n/a if no valid data
  const salesGoal = hasValidSalesGoal 
    ? new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(totalSalesGoal)
    : 'n/a';

  return {
    rooms: totalRooms,
    rn: totalRN,
    revenue: formattedRevenue,
    salesGoal: salesGoal
  };
};

/**
 * Format year-specific display data
 * @param {Object} totals - Totals object from calculateYearSpecificTotals
 * @param {String} activeManager - Active manager name
 * @returns {Object} Formatted display data
 */
export const formatYearSpecificDisplay = (totals, activeManager) => {
  return {
    manager: activeManager,
    rooms: totals.rooms.toLocaleString(),
    rn: totals.rn.toLocaleString(),
    revenue: totals.revenue,
    salesGoal: totals.salesGoal
  };
};

/**
 * Calculate previous year revenue for a list of clients, given a selected year and available years.
 * @param {Array} clients - Array of client objects
 * @param {String} selectedYear - The year currently selected in the UI ('All' or a year string)
 * @param {Array} availableYears - Array of available year strings, sorted ascending (e.g. ['2022', '2023', '2024'])
 * @returns {String} Formatted previous year revenue or 'n/a' if not available
 */
export const calculatePreviousYearRevenueForYear = (clients, selectedYear, availableYears) => {
  if (!clients || clients.length === 0 || !availableYears || availableYears.length === 0) {
    return 'n/a';
  }

  // If "All" is selected, use the latest year as the reference
  let referenceYear;
  if (selectedYear === 'All') {
    referenceYear = availableYears[availableYears.length - 1];
  } else {
    referenceYear = selectedYear;
  }

  // Find the index of the reference year
  const refIndex = availableYears.indexOf(referenceYear);

  // If the reference year is the first year, there is no previous year
  if (refIndex <= 0) {
    return 'n/a';
  }

  const previousYear = availableYears[refIndex - 1];

  let totalPreviousRevenue = 0;

  clients.forEach(client => {
    const previousYearData = getYearSpecificData(client, previousYear);
    const revenueStr = previousYearData.revenue.toString().replace(/[$,]/g, '');
    totalPreviousRevenue += parseFloat(revenueStr) || 0;
  });

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(totalPreviousRevenue);
};

/**
 * Retention analysis utilities for pinned client list
 */

/**
 * Returns true if the client has bookings in the given year.
 * @param {Object} client
 * @param {String} year
 * @returns {Boolean}
 */
export function clientHasBookingInYear(client, year) {
  if (!client || !year) return false;
  const rooms = client[`${year} Rooms`];
  const rn = client[`${year} RN`];
  const revenue = client[`${year} Revenue`];
  // Consider a booking if any of these is non-zero/non-empty
  return (
    (typeof rooms === 'number' && rooms > 0) ||
    (typeof rooms === 'string' && parseFloat(rooms) > 0) ||
    (typeof rn === 'number' && rn > 0) ||
    (typeof rn === 'string' && parseFloat(rn) > 0) ||
    (typeof revenue === 'number' && revenue > 0) ||
    (typeof revenue === 'string' && parseFloat(revenue.replace(/[$,]/g, '')) > 0)
  );
}

/**
 * Returns true if the client has bookings in ANY year before the given year.
 * @param {Object} client
 * @param {String} year
 * @param {Array} availableYears - sorted ascending
 * @returns {Boolean}
 */
export function clientHasBookingBeforeYear(client, year, availableYears) {
  if (!client || !year || !availableYears) return false;
  const idx = availableYears.indexOf(year);
  if (idx <= 0) return false;
  for (let i = 0; i < idx; i++) {
    if (clientHasBookingInYear(client, availableYears[i])) return true;
  }
  return false;
}

/**
 * Returns true if the client has bookings in BOTH the given year and the previous year.
 * @param {Object} client
 * @param {String} year
 * @param {Array} availableYears - sorted ascending
 * @returns {Boolean}
 */
export function clientIsRetained(client, year, availableYears) {
  if (!client || !year || !availableYears) return false;
  const idx = availableYears.indexOf(year);
  if (idx <= 0) return false;
  const prevYear = availableYears[idx - 1];
  return clientHasBookingInYear(client, year) && clientHasBookingInYear(client, prevYear);
}

/**
 * Returns true if the client is "lost" (booked in previous year, but NOT in the selected year).
 * @param {Object} client
 * @param {String} year
 * @param {Array} availableYears - sorted ascending
 * @returns {Boolean}
 */
/**
 * Returns true if the client is "lost" (booked in previous year, but NOT in the selected year).
 * This version is for use when comparing the full set of clients from both years.
 * @param {Object} prevYearClient - client object from previous year
 * @param {Array} selectedYearClients - array of client objects for selected year
 * @param {String} prevYear
 * @param {String} selectedYear
 * @returns {Boolean}
 */
export function clientIsLostFull(prevYearClient, selectedYearClients, prevYear, selectedYear) {
  // Find matching client in selected year by unique key (e.g., name+country)
  const key = `${prevYearClient.client_name}__${prevYearClient.country}`;
  const match = selectedYearClients.find(
    c => `${c.client_name}__${c.country}` === key
  );
  // Lost if booked in prevYear, and either missing in selected year or not booked in selected year
  return (
    clientHasBookingInYear(prevYearClient, prevYear) &&
    (!match || !clientHasBookingInYear(match, selectedYear))
  );
}

// The old clientIsLost is now only used for legacy filtering, but not for the new "lost" logic.
export function clientIsLost(client, year, availableYears) {
  if (!client || !year || !availableYears) return false;
  const idx = availableYears.indexOf(year);
  if (idx <= 0) return false; // No previous year
  const prevYear = availableYears[idx - 1];
  // DEBUG: Log booking years for this client
  if (typeof window !== "undefined" && window.__DEBUG_RETENTION) {
    const bookings = availableYears.map(y => ({
      y,
      has: clientHasBookingInYear(client, y)
    }));
    // eslint-disable-next-line no-console
    console.log("clientIsLost", { client, year, prevYear, availableYears, bookings });
  }
  return clientHasBookingInYear(client, prevYear) && !clientHasBookingInYear(client, year);
}

/**
 * Calculate new clients percentage for a manager and year.
 * @param {Array} clientData - all client data
 * @param {String} selectedYear
 * @param {String} activeManager
 * @returns {number} newClientsPercent
 */
export function calculateNewClientsPercent(clientData, selectedYear, activeManager) {
  if (!clientData || !selectedYear || !activeManager) return 0;
  // Find available years
  const yearsSet = new Set();
  clientData.forEach(client => {
    Object.keys(client).forEach(key => {
      const match = key.match(/^(\d{4}) Revenue$/);
      if (match) yearsSet.add(match[1]);
    });
  });
  const availableYears = Array.from(yearsSet).sort();
  const idx = availableYears.indexOf(selectedYear);
  if (idx < 0) return 0;
  // Only include clients for the selected manager
  const managerClients = clientData.filter(
    c => c.conference_manager === activeManager
  );
  // All clients with bookings in selected year
  const bookedCurrent = managerClients.filter(c => (
    c[`${selectedYear} Rooms`] > 0 ||
    c[`${selectedYear} RN`] > 0 ||
    (typeof c[`${selectedYear} Revenue`] === "string" && parseFloat(c[`${selectedYear} Revenue`].replace(/[$,]/g, "")) > 0)
  ));
  // New clients: booked in selected year, not in any previous year
  const newClients = bookedCurrent.filter(c =>
    !clientHasBookingBeforeYear(c, selectedYear, availableYears)
  );
  const percent = bookedCurrent.length > 0 ? Math.round((newClients.length / bookedCurrent.length) * 100) : 0;
  return percent;
}

/**
 * Calculate retention and lost percentages for a manager and year.
 * @param {Array} clientData - all client data
 * @param {String} selectedYear
 * @param {String} activeManager
 * @returns {{retainedPercent: number, lostPercent: number}}
 */
export function calculateRetentionPercentages(clientData, selectedYear, activeManager) {
  if (!clientData || !selectedYear || !activeManager) return { retainedPercent: 0, lostPercent: 0 };
  // Find available years
  const yearsSet = new Set();
  clientData.forEach(client => {
    Object.keys(client).forEach(key => {
      const match = key.match(/^(\d{4}) Revenue$/);
      if (match) yearsSet.add(match[1]);
    });
  });
  const availableYears = Array.from(yearsSet).sort();
  const idx = availableYears.indexOf(selectedYear);
  if (idx > 0) {
    const prevYear = availableYears[idx - 1];
    const managerClients = clientData.filter(
      c => c.conference_manager === activeManager
    );
    const prevYearClients = managerClients.filter(c => (
      c[`${prevYear} Rooms`] > 0 ||
      c[`${prevYear} RN`] > 0 ||
      (typeof c[`${prevYear} Revenue`] === "string" && parseFloat(c[`${prevYear} Revenue`].replace(/[$,]/g, "")) > 0)
    ));
    const selectedYearClients = managerClients.filter(c => (
      c[`${selectedYear} Rooms`] > 0 ||
      c[`${selectedYear} RN`] > 0 ||
      (typeof c[`${selectedYear} Revenue`] === "string" && parseFloat(c[`${selectedYear} Revenue`].replace(/[$,]/g, "")) > 0)
    ));
    // Retained: booked in both years
    const retained = prevYearClients.filter(prevClient => {
      const key = `${prevClient.client_name}__${prevClient.country}`;
      const match = selectedYearClients.find(
        c => `${c.client_name}__${c.country}` === key
      );
      return match;
    }).length;
    const lost = prevYearClients.length - retained;
    const prevTotal = prevYearClients.length;
    // DEBUG: Log for troubleshooting
    if (typeof window !== "undefined" && window.__DEBUG_RETENTION) {
      // eslint-disable-next-line no-console
      console.log("Retention % debug:", {
        prevYear,
        selectedYear,
        prevYearClients: prevYearClients.map(c => c.client_name),
        selectedYearClients: selectedYearClients.map(c => c.client_name),
        prevTotal,
        retained,
        lost,
        retainedPercent: prevTotal > 0 ? Math.round((retained / prevTotal) * 100) : 0,
        lostPercent: prevTotal > 0 ? Math.round((lost / prevTotal) * 100) : 0
      });
    }
    return {
      retainedPercent: prevTotal > 0 ? Math.round((retained / prevTotal) * 100) : 0,
      lostPercent: prevTotal > 0 ? Math.round((lost / prevTotal) * 100) : 0
    };
  }
  return { retainedPercent: 0, lostPercent: 0 };
}

/**
 * Filters clients by retention type.
 * @param {Array} clients
 * @param {String} year
 * @param {Array} availableYears
 * @param {String} retentionType - 'default' | 'lost' | 'retained' | 'both'
 * @returns {Array}
 */
export function filterClientsByRetention(clients, year, availableYears, retentionType) {
  // DEBUG: Log filter invocation
  if (typeof window !== "undefined" && window.__DEBUG_RETENTION) {
    // eslint-disable-next-line no-console
    console.log("filterClientsByRetention", {
      count: Array.isArray(clients) ? clients.length : 0,
      year,
      availableYears,
      retentionType
    });
    // Print bookings for first 3 clients
    if (Array.isArray(clients)) {
      clients.slice(0, 3).forEach((c, idx) => {
        const bookings = availableYears.map(y => ({
          y,
          has: clientHasBookingInYear(c, y)
        }));
        // eslint-disable-next-line no-console
        console.log(`Client[${idx}] bookings:`, bookings);
      });
      // Check if any client is eligible to be "lost"
      const lostCount = clients.filter(c => clientIsLost(c, year, availableYears)).length;
      // eslint-disable-next-line no-console
      console.log("Lost clients count (debug):", lostCount);
    }
  }
  if (!Array.isArray(clients) || !year || !availableYears || !retentionType) return clients;
  switch (retentionType) {
    case 'lost': {
      // New logic: compare previous year's clients to selected year's clients
      const idx = availableYears.indexOf(year);
      const prevYear = idx > 0 ? availableYears[idx - 1] : null;
      if (!prevYear) return [];
      // Get all clients for previous year and selected year
      // "clients" is the set for the selected year (current), but we need both sets
      // Assume clients is the union of all clients, so we need to filter for prevYear and selectedYear
      // In practice, this should be called with the union of all clients for the manager
      const prevYearClients = clients.filter(c => clientHasBookingInYear(c, prevYear));
      const selectedYearClients = clients.filter(c => clientHasBookingInYear(c, year));
      const lostClients = prevYearClients.filter(prevClient =>
        clientIsLostFull(prevClient, selectedYearClients, prevYear, year)
      );
      if (typeof window !== "undefined" && window.__DEBUG_RETENTION) {
        // eslint-disable-next-line no-console
        console.log("Lost clients (debug):", lostClients.map(c => c.client_name));
        // Also print retained for comparison
        const retainedClients = clients.filter(c => clientIsRetained(c, year, availableYears));
        // eslint-disable-next-line no-console
        console.log("Retained clients (debug):", retainedClients.map(c => c.client_name));
      }
      return lostClients;
    }
    case 'retained':
      return clients.filter(c => clientIsRetained(c, year, availableYears));
    case 'new':
      // Clients who have a booking in the selected year, but not in any previous year
      return clients.filter(
        c => clientHasBookingInYear(c, year) && !clientHasBookingBeforeYear(c, year, availableYears)
      );
    // "both" removed
    case 'default':
    default:
      return clients;
  }
}

/**
 * Get current year and previous year based on selection
 * @param {string} selectedYear - Selected year or "All"
 * @returns {Object} Object with year and prevYear properties
 */
export const getYearAndPrevious = (selectedYear) => {
  const year = selectedYear && selectedYear !== "All" 
    ? selectedYear 
    : getConfiguredAllYearsYear();
  const prevYear = (parseInt(year, 10) - 1).toString();
  return { year, prevYear };
};

/**
 * Get year-specific revenue data for a client
 * @param {Object} client - Client object
 * @param {string} year - Current year
 * @param {string} prevYear - Previous year
 * @returns {Object} Object with revenue values
 */
export const getYearSpecificRevenue = (client, year, prevYear) => {
  return {
    prevYearRevenue: client[`${prevYear} Revenue`] || "n/a",
    currYearRevenue: client[`${year} Revenue`] || "n/a",
    totalRevenue: client["Total Revenue"] || "n/a"
  };
};

/**
 * Get year-specific room data for a client
 * @param {Object} client - Client object
 * @param {string} year - Current year
 * @param {string} prevYear - Previous year
 * @returns {Object} Object with room values
 */
export const getYearSpecificRooms = (client, year, prevYear) => {
  return {
    prevYearRooms: client[`${prevYear} Rooms`] || "n/a",
    currYearRooms: client[`${year} Rooms`] || "n/a",
    totalRooms: client.group_size || client["Total Rooms"] || "n/a"
  };
};
